import React from 'react';
import { ScrollView, TouchableOpacity, View, Text, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../styles/theme';
import { availableChips, type ShortcutChipId } from './searchShortcuts';

export type { ShortcutChipId };

interface SearchShortcutChipsProps {
  /** Ids of every currently applied filter, used to hide redundant chips. */
  activeTokenIds: string[];
  onChipPress: (chipId: ShortcutChipId) => void;
  /**
   * Rendered first, inside the same scroll. The Recipes tab puts its Filters
   * button here: it used to take a row of its own under the chips, one button
   * in a different shape from the row above it.
   */
  leading?: React.ReactNode;
}

/**
 * Suggestions for filters that are *not* yet applied.
 *
 * A chip disappears from this row the moment its filter is active, and
 * reappears when it is removed. Applied filters are rendered once, as removable
 * pills, by the caller. This row previously rendered an active chip in a dark
 * "on" state while the same filter also appeared as a pill below — one filter,
 * two controls, two different removal gestures.
 */
export const SearchShortcutChips: React.FC<SearchShortcutChipsProps> = ({
  activeTokenIds,
  onChipPress,
  leading,
}) => {
  const { t } = useTranslation();
  const available = availableChips(activeTokenIds);

  // Every suggestion is applied — the row would be an empty band of padding.
  if (available.length === 0 && !leading) return null;

  return (
    <View style={styles.wrapper}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
        style={styles.scroll}
      >
        {leading}
        {available.map((chip) => (
          <TouchableOpacity
            key={chip.id}
            style={styles.chip}
            onPress={() => onChipPress(chip.id)}
            activeOpacity={0.7}
          >
            {'dotColor' in chip && chip.dotColor ? (
              <View style={[styles.dot, { backgroundColor: chip.dotColor }]} />
            ) : chip.icon ? (
              <Feather name={chip.icon} size={13} color={Paprika.textSecondary} />
            ) : null}
            <Text style={styles.label}>{t(chip.labelKey)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      {/*
        Signals that the row continues past the edge. Without it the last chip is
        sliced mid-word and reads as broken layout rather than scrollable
        content. Non-interactive so it never swallows a tap on the chip beneath.
      */}
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(251,249,246,0)', Paprika.background]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.edgeFade}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  scroll: {
    flexGrow: 0,
  },
  row: {
    flexDirection: 'row',
    paddingHorizontal: 18,
    paddingBottom: 12,
    gap: 8,
  },
  edgeFade: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 12,
    width: 40,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 36,
    paddingHorizontal: 13,
    borderRadius: 18,
    // Tonal, like the search bar above it — DESIGN.md has no 1px outlines.
    backgroundColor: Paprika.bgSunk,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
});
